import { useState, useEffect } from "react";
import api from "../lib/api";
import { useWorkspace } from "../lib/workspaceContext";
import { Plus, Trash2, Lightbulb, Star, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function IdeaVault() {
  const { currentId } = useWorkspace();
  const [ideas, setIdeas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ workspace_id: currentId, title: "", hook: "", structure: "", platform: "tiktok", priority: "medium", viral_score: 0, status: "new", notes: "" });
  const [scoringId, setScoringId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const all = await api.listIdeas();
    setIdeas(all.filter(i => (currentId ? i.workspace_id === currentId : true)));
  };
  useEffect(() => { load(); setForm(f => ({ ...f, workspace_id: currentId })); }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    await api.createIdea({ ...form, workspace_id: currentId });
    toast.success("Idée enregistrée");
    setShowForm(false);
    setForm({ workspace_id: currentId, title: "", hook: "", structure: "", platform: "tiktok", priority: "medium", viral_score: 0, status: "new", notes: "" });
    load();
  };

  const del = async (id) => { await api.deleteIdea(id); load(); };
  const updateStatus = async (i, status) => { await api.updateIdea(i.id, { status }); load(); };

  const scoreIdea = async (idea) => {
    setScoringId(idea.id);
    try {
      const r = await api.aiScoreVirality({ hook: idea.hook || idea.title, concept: idea.structure, platform: idea.platform });
      const m = (r.raw || "").match(/\{[\s\S]*\}/);
      if (m) {
        const parsed = JSON.parse(m[0]);
        await api.updateIdea(idea.id, { viral_score: parsed.global_score || 0 });
        toast.success(`Score : ${parsed.global_score}`);
        load();
      }
    } catch { toast.error("Erreur scoring"); }
    finally { setScoringId(null); }
  };

  const filtered = filter === "all" ? ideas : ideas.filter(i => i.status === filter);
  const sorted = [...filtered].sort((a, b) => b.viral_score - a.viral_score);

  const STATUS = { new: "#00D4FF", in_progress: "#F59E0B", done: "#10B981", discarded: "#52525B" };
  const PRIO = { low: "#52525B", medium: "#CCFF00", high: "#EF4444" };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="idea-vault-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="border-l-2 border-[#CCFF00] pl-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// IDEA VAULT</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Coffre à idées</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">Stocke, score, prioritise. Plus jamais d'idée perdue.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-idea-btn"
          className="bg-[#CCFF00] text-black px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Idée
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        {["all", "new", "in_progress", "done", "discarded"].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`idea-filter-${s}`}
            className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider transition ${
              filter === s ? "bg-[#CCFF00] text-black" : "border border-[#1E1E24] text-zinc-400 hover:text-white"
            }`}>{s === "all" ? "Toutes" : s}</button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="idea-form">
          <input required placeholder="Titre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <input placeholder="Hook" value={form.hook} onChange={e => setForm({ ...form, hook: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <textarea placeholder="Structure (0-1s hook · 1-3s tension · ...)" value={form.structure} onChange={e => setForm({ ...form, structure: e.target.value })} rows={2} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="tiktok">TikTok</option><option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option><option value="threads">Threads</option><option value="x">X</option>
          </select>
          <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="low">Priorité basse</option>
            <option value="medium">Priorité moyenne</option>
            <option value="high">Priorité haute</option>
          </select>
          <button type="submit" data-testid="submit-idea" className="bg-[#CCFF00] text-black px-5 py-2 rounded-sm font-medium text-sm md:col-span-2">Enregistrer</button>
        </form>
      )}

      {sorted.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Lightbulb className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucune idée pour le moment.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(i => (
            <div key={i.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 hover:border-white/20 transition" data-testid={`idea-${i.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium leading-tight">{i.title}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm" style={{ color: PRIO[i.priority], background: `${PRIO[i.priority]}10`, border: `1px solid ${PRIO[i.priority]}40` }}>{i.priority}</span>
                    <span className="text-[9px] font-mono text-zinc-500 uppercase">{i.platform}</span>
                  </div>
                </div>
                <button onClick={() => del(i.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {i.hook && <div className="border-l-2 border-[#CCFF00] pl-3 mb-2 text-sm text-white italic">"{i.hook}"</div>}
              {i.structure && <div className="text-xs text-zinc-400 mb-3 whitespace-pre-line line-clamp-2">{i.structure}</div>}
              <div className="flex items-center justify-between pt-3 border-t border-[#1E1E24]">
                <div className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-[#CCFF00]" />
                  <span className="font-mono text-sm text-[#CCFF00]">{i.viral_score}</span>
                </div>
                <button onClick={() => scoreIdea(i)} disabled={scoringId === i.id} data-testid={`score-idea-${i.id}`}
                  className="text-[10px] font-mono uppercase tracking-wider text-[#00D4FF] hover:text-[#CCFF00] flex items-center gap-1">
                  {scoringId === i.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Score IA
                </button>
              </div>
              <select value={i.status} onChange={e => updateStatus(i, e.target.value)}
                className="mt-2 w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-2 py-1 text-[11px] font-mono text-white"
                style={{ color: STATUS[i.status] }}>
                <option value="new">New</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
                <option value="discarded">Discarded</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
